'use client';

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { X, ChevronRight, ChevronLeft, Volume2, VolumeX } from 'lucide-react';

interface StoryPage {
  pageNumber: number;
  text: string;
  illustration?: string;
}

interface PublicStoryPreviewProps {
  isOpen: boolean;
  onClose: () => void;
  storyTitle: string;
  storyDescription: string;
  previewPages: StoryPage[];
  totalPages: number;
  onGetStarted: () => void;
}

export default function PublicStoryPreview({
  isOpen,
  onClose,
  storyTitle,
  storyDescription,
  previewPages,
  totalPages,
  onGetStarted
}: PublicStoryPreviewProps) {
  const [currentPage, setCurrentPage] = useState(0);
  const [isNarrating, setIsNarrating] = useState(false);

  // Stop narration when page changes or modal closes
  useEffect(() => {
    if (isNarrating) {
      window.speechSynthesis.cancel();
      setIsNarrating(false);
    }
  }, [currentPage]);

  useEffect(() => {
    if (!isOpen && isNarrating) {
      window.speechSynthesis.cancel();
      setIsNarrating(false);
    }
  }, [isOpen, isNarrating]);

  if (!isOpen) return null;

  const nextPage = () => {
    if (currentPage < previewPages.length - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNarration = () => {
    if (isNarrating) {
      // Stop narration
      window.speechSynthesis.cancel();
      setIsNarrating(false);
    } else {
      // Start narration
      const utterance = new SpeechSynthesisUtterance(previewPages[currentPage]?.text || '');
      utterance.rate = 0.8; // Slower for children
      utterance.pitch = 1.1; // Slightly higher pitch for storytelling
      utterance.volume = 0.9;

      utterance.onstart = () => setIsNarrating(true);
      utterance.onend = () => setIsNarrating(false);
      utterance.onerror = () => setIsNarrating(false);

      window.speechSynthesis.speak(utterance);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="bg-white w-full max-w-4xl max-h-[90vh] overflow-hidden relative">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-cyan-400 text-white p-6 relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 bg-white/20 hover:bg-white/30 rounded-full p-2 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>

          <div className="pr-12">
            <div className="flex items-center gap-2 mb-2">
              <span className="bg-white/20 text-white text-sm px-3 py-1 rounded-full font-medium">
                📖 FREE PREVIEW
              </span>
              <span className="bg-green-500/20 text-white text-sm px-3 py-1 rounded-full font-medium flex items-center gap-1">
                <Volume2 className="h-3 w-3" />
                VOICE NARRATION
              </span>
            </div>
            <h2 className="font-bungee text-2xl mb-2">"{storyTitle}"</h2>
            <p className="text-white/90 text-sm">{storyDescription}</p>
          </div>
        </div>

        {/* Story Reader */}
        <div className="flex flex-col h-[500px]">
          {/* Page Content */}
          <div className="flex-1 grid md:grid-cols-2 min-h-0">
            {/* Text Content */}
            <div className="p-8 flex flex-col justify-center bg-gradient-to-br from-blue-50 to-cyan-50">
              <div className="flex items-center justify-between mb-4">
                <span className="bg-blue-100 text-blue-800 text-sm px-3 py-1 rounded-full font-medium">
                  Page {previewPages[currentPage]?.pageNumber || 1}
                </span>
                <Button
                  onClick={handleNarration}
                  size="sm"
                  variant={isNarrating ? "default" : "outline"}
                  className={`flex items-center gap-2 ${
                    isNarrating
                      ? 'bg-green-500 hover:bg-green-600 text-white animate-pulse'
                      : 'hover:bg-blue-50 text-blue-600 border-blue-300'
                  }`}
                  title={isNarrating ? "Stop narration" : "Play voice narration"}
                >
                  {isNarrating ? (
                    <>
                      <VolumeX className="h-4 w-4" />
                      <span className="text-xs">Stop</span>
                    </>
                  ) : (
                    <>
                      <Volume2 className="h-4 w-4" />
                      <span className="text-xs">Listen</span>
                    </>
                  )}
                </Button>
              </div>
              <p className="text-xl text-slate-800 leading-relaxed font-medium">
                {previewPages[currentPage]?.text || ''}
              </p>
            </div>

            {/* Illustration */}
            <div className="bg-gradient-to-br from-yellow-100 to-orange-100 p-8 flex items-center justify-center">
              {previewPages[currentPage]?.illustration ? (
                <div className="text-8xl animate-bounce">
                  {previewPages[currentPage]?.illustration}
                </div>
              ) : (
                <div className="text-center text-slate-600">
                  <div className="text-6xl mb-4">📚</div>
                  <p className="text-sm opacity-75">Colorful illustration goes here</p>
                </div>
              )}
            </div>
          </div>

          {/* Navigation */}
          <div className="border-t bg-white p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button
                  onClick={prevPage}
                  disabled={currentPage === 0}
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-2"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>

                <Button
                  onClick={nextPage}
                  disabled={currentPage === previewPages.length - 1}
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-2"
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>

              <div className="text-sm text-slate-600">
                {currentPage + 1} of {previewPages.length} preview pages
                <span className="opacity-60"> • {totalPages} total pages</span>
              </div>
            </div>
          </div>

          {/* Call to Action */}
          {currentPage === previewPages.length - 1 && (
            <div className="bg-gradient-to-r from-green-500 to-blue-500 text-white p-6 text-center">
              <h3 className="font-bold text-lg mb-2">Want to read the complete story?</h3>
              <p className="text-white/90 mb-4 text-sm">
                Get access to all {totalPages} pages plus audio narration and hundreds more stories!
              </p>
              <Button
                onClick={onGetStarted}
                className="bg-white text-blue-600 hover:bg-blue-50 font-bold px-6 py-2 rounded-full"
              >
                Start Free Trial
              </Button>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}